import React, { useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { formatMoney } from '@/utils/format';
import { EpcEstimateMaterialBuildUpPanel } from './EpcEstimateMaterialBuildUpPanel';
import { getEst3CostProvenance } from './est3CostProvenance';

const { Text } = Typography;

export interface EpcEstimateMaterialsWorkbenchProps {
  estimateId: string;
  revisionId: string;
  readOnly: boolean;
  materialLines: any[];
  loading?: boolean;
  onOpenAdd?: () => void;
  onCostingChanged?: () => void;
}

/**
 * EST3 UX2 Wave 4 — Materials depth: master-detail lines + landed build-up panel.
 */
export const EpcEstimateMaterialsWorkbench: React.FC<EpcEstimateMaterialsWorkbenchProps> = ({
  estimateId,
  revisionId,
  readOnly,
  materialLines,
  loading,
  onOpenAdd,
  onCostingChanged,
}) => {
  const [selectedLineId, setSelectedLineId] = useState<string | null>(null);

  const selectedLine = useMemo(
    () => materialLines.find((l) => l.id === selectedLineId) || null,
    [materialLines, selectedLineId]
  );

  const totalMaterial = useMemo(
    () => materialLines.reduce((acc, l) => acc + (Number(l.total_amount) || 0), 0),
    [materialLines]
  );

  return (
    <Card size="small" title="Materials">
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Statistic
              title="Material lines"
              value={materialLines.length}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic
              title="Material cost total"
              value={totalMaterial}
              formatter={(v) => formatMoney(v, { display: 'plain', maximumFractionDigits: 0 })}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic title="Selected" value={selectedLine ? 1 : 0} suffix={selectedLine ? 'line' : ''} />
          </Col>
        </Row>

        {!readOnly && onOpenAdd ? (
          <Button type="primary" icon={<PlusOutlined />} onClick={onOpenAdd}>
            Add material cost line
          </Button>
        ) : null}

        <Row gutter={16}>
          <Col xs={24} lg={10}>
            <Table
              size="small"
              rowKey="id"
              loading={loading}
              dataSource={materialLines}
              pagination={materialLines.length > 25 ? { pageSize: 25 } : false}
              rowSelection={{
                type: 'radio',
                selectedRowKeys: selectedLineId ? [selectedLineId] : [],
                onChange: (keys) => setSelectedLineId((keys[0] as string) || null),
              }}
              onRow={(row) => ({
                onClick: () => setSelectedLineId(row.id),
                style: { cursor: 'pointer' },
              })}
              columns={[
                { title: 'Description', dataIndex: 'description', ellipsis: true },
                {
                  title: 'Total',
                  dataIndex: 'total_amount',
                  width: 100,
                  render: (v) => formatMoney(v, { display: 'plain', maximumFractionDigits: 0 }),
                },
                {
                  title: 'Prov.',
                  width: 100,
                  ellipsis: true,
                  render: (_: unknown, row: any) => {
                    const p = getEst3CostProvenance(row);
                    return p.summary !== '—' ? <Tag color="blue">{p.summary}</Tag> : '—';
                  },
                },
              ]}
            />
          </Col>
          <Col xs={24} lg={14}>
            <EpcEstimateMaterialBuildUpPanel
              estimateId={estimateId}
              revisionId={revisionId}
              costLineId={selectedLineId}
              costLine={selectedLine}
              readOnly={readOnly}
              onChanged={onCostingChanged}
            />
          </Col>
        </Row>
      </Space>
    </Card>
  );
};
